import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Save, Trash2, Upload, Music, FileText, Image as ImageIcon, ExternalLink } from "lucide-react";

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;
const toAbs = (u) => (u && u.startsWith("/") ? `${API_ORIGIN}${u}` : u);

function useArtists() {
  const [list, setList] = useState([]);
  const refresh = () => api.get("/artists").then((r) => setList(r.data));
  useEffect(() => { refresh(); }, []);
  return [list, refresh];
}

export function AdminArtistsList() {
  const [artists, refresh] = useArtists();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", medium: "", site: "", image: "" });
  const nav = useNavigate();

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/artists", form);
      toast.success("Artiste créé");
      setCreating(false);
      setForm({ name: "", medium: "", site: "", image: "" });
      refresh();
      nav(`/admin/artistes/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet artiste ?")) return;
    try {
      await api.delete(`/admin/artists/${id}`);
      toast.success("Artiste supprimé");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <main className="pt-32 pb-20 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="admin-artists-list">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <Link to="/admin" className="meta-label text-fg/60 link-underline"><ArrowLeft size={12} className="inline mr-1" /> Back-office</Link>
          <div className="meta-label text-cinabre mt-4">§ Gestion · Artistes</div>
          <h1 className="font-serif font-light text-5xl md:text-6xl leading-[0.9] tracking-tight mt-3">{artists.length} artistes.</h1>
        </div>
        <button onClick={() => setCreating((v) => !v)} className="btn-editorial" data-testid="admin-new-artist">
          <Plus size={14} /> {creating ? "Annuler" : "Nouvel artiste"}
        </button>
      </div>

      {creating && (
        <form onSubmit={create} className="mt-10 border border-fg/15 p-6 md:p-8 grid md:grid-cols-4 gap-4" data-testid="admin-new-artist-form">
          <input required placeholder="Nom *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre" data-testid="new-artist-name" />
          <input placeholder="Médium (Peinture, Slam…)" value={form.medium} onChange={(e) => setForm({ ...form, medium: e.target.value })} className="border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre" />
          <input placeholder="Site (Toulouse, Loupian…)" value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} className="border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre" />
          <input placeholder="URL portrait (https://…)" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre" />
          <div className="md:col-span-4">
            <button type="submit" className="btn-editorial" data-testid="new-artist-submit">Créer</button>
          </div>
        </form>
      )}

      <table className="w-full mt-12 text-sm" data-testid="admin-artists-table">
        <thead>
          <tr className="border-b border-fg/20 text-left">
            <th className="py-3 meta-label text-fg/60">Nom</th>
            <th className="py-3 meta-label text-fg/60">Slug</th>
            <th className="py-3 meta-label text-fg/60">Médium</th>
            <th className="py-3 meta-label text-fg/60">Site</th>
            <th className="py-3 meta-label text-fg/60">Œuvres</th>
            <th className="py-3" />
          </tr>
        </thead>
        <tbody>
          {artists.map((a) => (
            <tr key={a.id} className="border-b border-fg/10 hover:bg-fg/[0.03]">
              <td className="py-3 font-serif text-lg">{a.name}</td>
              <td className="py-3 font-mono text-xs text-fg/60">{a.slug}</td>
              <td className="py-3">{a.medium || "—"}</td>
              <td className="py-3">{a.site || "—"}</td>
              <td className="py-3 font-mono text-xs">{(a.works || []).length}</td>
              <td className="py-3 text-right">
                <Link to={`/artistes/${a.slug}`} target="_blank" className="inline-flex items-center gap-2 mr-4 link-underline font-mono text-[10px] tracking-[0.2em] uppercase">
                  <ExternalLink size={12} /> Voir
                </Link>
                <Link to={`/admin/artistes/${a.id}`} className="inline-flex items-center gap-2 mr-4 link-underline font-mono text-[10px] tracking-[0.2em] uppercase text-cinabre" data-testid={`admin-edit-${a.slug}`}>
                  Éditer
                </Link>
                <button onClick={() => remove(a.id)} className="link-underline font-mono text-[10px] tracking-[0.2em] uppercase text-fg/60">
                  <Trash2 size={12} className="inline" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}

function WorkForm({ artistId, onCreated }) {
  const [kind, setKind] = useState("audio");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUrl(data.url);
      if (data.kind === "midi") setKind("midi");
      else if (data.kind === "audio") setKind("audio");
      else if (data.kind === "image") setKind("image");
      toast.success("Fichier chargé");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/artists/${artistId}/works`, {
        kind, title, description,
        text: kind === "text" ? text : "",
        url: kind === "text" ? "" : url,
      });
      toast.success("Œuvre ajoutée");
      setTitle(""); setDescription(""); setText(""); setUrl("");
      onCreated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <form onSubmit={submit} className="border border-fg/15 p-6 md:p-8 space-y-6" data-testid="admin-work-form">
      <div className="flex gap-2 flex-wrap">
        {[
          { k: "audio", l: "Audio (slam, MP3)", Icon: Music },
          { k: "midi",  l: "MIDI",              Icon: Music },
          { k: "image", l: "Image · Œuvre",     Icon: ImageIcon },
          { k: "text",  l: "Texte · Écriture",  Icon: FileText },
        ].map(({ k, l, Icon }) => (
          <button
            type="button"
            key={k}
            onClick={() => setKind(k)}
            className={`px-4 py-2 border font-mono text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-2 transition-colors ${kind === k ? "bg-fg text-bg border-fg" : "border-fg/25 hover:bg-fg hover:text-bg"}`}
            data-testid={`work-kind-${k}`}
          >
            <Icon size={12} /> {l}
          </button>
        ))}
      </div>

      <input
        placeholder="Titre de l'œuvre"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full border-b border-fg/30 bg-transparent py-3 font-serif text-2xl focus:outline-none focus:border-cinabre"
        data-testid="work-title"
      />

      {kind === "text" ? (
        <textarea
          required
          rows={8}
          placeholder="Écriture, poème, essai…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full border border-fg/20 bg-transparent p-4 font-serif text-xl leading-relaxed focus:outline-none focus:border-cinabre resize-vertical"
          data-testid="work-text"
        />
      ) : (
        <div className="flex flex-col md:flex-row gap-3 md:items-end">
          <input
            placeholder="URL du fichier (ou téléverser ci-contre)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="flex-1 border-b border-fg/30 bg-transparent py-3 font-serif text-lg focus:outline-none focus:border-cinabre"
            data-testid="work-url"
          />
          <label className="btn-editorial ghost cursor-pointer">
            <Upload size={14} /> {uploading ? "Envoi…" : "Téléverser"}
            <input type="file" onChange={upload} className="hidden"
              accept={kind === "midi" ? ".mid,.midi" : kind === "image" ? "image/*" : kind === "audio" ? "audio/*" : undefined}
              data-testid="work-file" />
          </label>
        </div>
      )}

      <input
        placeholder="Description (optionnel)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="w-full border-b border-fg/30 bg-transparent py-3 font-serif text-lg focus:outline-none focus:border-cinabre"
      />

      <button type="submit" className="btn-editorial" data-testid="work-submit">
        <Plus size={14} /> Ajouter l'œuvre
      </button>
    </form>
  );
}

export function AdminArtistEdit() {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [form, setForm] = useState({ name: "", medium: "", site: "", image: "", country: "", bio: "", website: "", instagram: "" });

  const load = async () => {
    const { data } = await api.get("/artists");
    const a = data.find((x) => x.id === id);
    setArtist(a || null);
    if (a) setForm({
      name: a.name || "", medium: a.medium || "", site: a.site || "",
      image: a.image || "", country: a.country || "", bio: a.bio || "",
      website: a.website || "", instagram: a.instagram || "",
    });
  };
  useEffect(() => { load(); }, [id]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/artists/${id}`, form);
      toast.success("Fiche sauvegardée");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteWork = async (workId) => {
    if (!window.confirm("Supprimer cette œuvre ?")) return;
    try {
      await api.delete(`/admin/artists/${id}/works/${workId}`);
      toast.success("Œuvre supprimée");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (!artist) return <main className="pt-40 px-6 md:px-12 max-w-[1200px] mx-auto"><div className="meta-label text-fg/50">Chargement…</div></main>;

  return (
    <main className="pt-32 pb-20 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="admin-artist-edit">
      <Link to="/admin/artistes" className="meta-label text-fg/60 link-underline"><ArrowLeft size={12} className="inline mr-1" /> Retour à la liste</Link>
      <div className="meta-label text-cinabre mt-4">§ Fiche artiste</div>
      <h1 className="font-serif font-light text-5xl md:text-6xl leading-[0.9] tracking-tight mt-3">{artist.name}</h1>
      <div className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
        <Link to={`/artistes/${artist.slug}`} target="_blank" className="link-underline">/artistes/{artist.slug}</Link>
      </div>

      <form onSubmit={save} className="mt-10 grid md:grid-cols-2 gap-8 border border-fg/15 p-6 md:p-8" data-testid="artist-edit-form">
        {[
          ["name", "Nom"],
          ["medium", "Médium"],
          ["site", "Site 2026"],
          ["country", "Pays d'origine"],
          ["image", "URL portrait"],
          ["website", "Site web"],
          ["instagram", "Instagram"],
        ].map(([k, l]) => (
          <label key={k} className="block">
            <span className="meta-label text-fg/60">{l}</span>
            <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre"
              data-testid={`field-${k}`} />
          </label>
        ))}
        <label className="md:col-span-2 block">
          <span className="meta-label text-fg/60">Bio</span>
          <textarea value={form.bio} rows={5} onChange={(e) => setForm({ ...form, bio: e.target.value })}
            className="mt-2 w-full border border-fg/20 bg-transparent p-4 font-serif text-lg focus:outline-none focus:border-cinabre resize-vertical"
            data-testid="field-bio" />
        </label>
        <div className="md:col-span-2">
          <button className="btn-editorial" data-testid="artist-save"><Save size={14} /> Sauvegarder</button>
        </div>
      </form>

      <div className="mt-16">
        <div className="meta-label text-cinabre">§ Œuvres — {(artist.works || []).length}</div>
        <h2 className="font-serif font-light text-4xl md:text-5xl mt-3 leading-tight tracking-tight">Ajouter une œuvre</h2>
        <div className="mt-8">
          <WorkForm artistId={id} onCreated={load} />
        </div>

        {(artist.works || []).length > 0 && (
          <div className="mt-12 space-y-4" data-testid="works-list">
            {artist.works.map((w) => (
              <div key={w.id} className="border border-fg/15 p-5 flex items-start justify-between gap-6" data-testid={`work-item-${w.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">{w.kind}</div>
                  <div className="font-serif text-2xl leading-tight mt-1">{w.title || "Sans titre"}</div>
                  {w.description && <div className="mt-1 text-fg/70">{w.description}</div>}
                  {w.kind === "text" && w.text && (
                    <div className="mt-2 font-serif italic text-fg/80 line-clamp-3">{w.text}</div>
                  )}
                  {w.url && (
                    <a href={toAbs(w.url)} target="_blank" rel="noreferrer" className="mt-2 inline-block link-underline font-mono text-[10px] tracking-[0.2em] uppercase text-fg/60">
                      {w.url}
                    </a>
                  )}
                </div>
                <button onClick={() => deleteWork(w.id)} className="text-fg/60 hover:text-cinabre" aria-label="Supprimer" data-testid={`work-delete-${w.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
